/**
 * Schema normalization helpers shared by every JSON-LD injector.
 *
 * Google's Organization / publisher validation requires `contentUrl` on the
 * `logo` ImageObject. Rather than editing hundreds of page-level schemas, we
 * normalize any `logo` field at injection time:
 *   - string  → { '@type': 'ImageObject', url, contentUrl }
 *   - object  → adds `contentUrl` mirroring `url` when absent
 */

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function normalizeLogoValue(value: unknown): unknown {
  if (typeof value === 'string' && value) {
    return { '@type': 'ImageObject', url: value, contentUrl: value };
  }
  if (isRecord(value)) {
    const url = typeof value.url === 'string' ? value.url : undefined;
    const contentUrl = typeof value.contentUrl === 'string' ? value.contentUrl : undefined;
    return {
      '@type': value['@type'] || 'ImageObject',
      ...value,
      ...(url && !contentUrl ? { contentUrl: url } : {}),
    };
  }
  return value;
}

/** Deep-clones the schema and guarantees `logo.contentUrl` everywhere. */
export function normalizeSchemaLogos<T>(schema: T): T {
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (!isRecord(node)) return node;
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      out[key] = key === 'logo' ? normalizeLogoValue(value) : walk(value);
    }
    return out;
  };
  return walk(schema) as T;
}
